import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, RadioGroup, Checkbox } from '../components/ui';
import { AddressForm } from '../components/forms/AddressForm';
import { FormSection } from '../components/layout';
import { maskCPF, maskDate, dateFromISO, dateToISO } from '../utils/masks';
import { pickErrors } from '../utils/formValidation';
import {
  PersonaFisicaForm as PersonaFisicaFormData,
  PersonIdentification,
  Address,
} from '../types';

const BRAZILIAN_STATES = [
  { value: 'AC', label: 'AC' }, { value: 'AL', label: 'AL' }, { value: 'AP', label: 'AP' },
  { value: 'AM', label: 'AM' }, { value: 'BA', label: 'BA' }, { value: 'CE', label: 'CE' },
  { value: 'DF', label: 'DF' }, { value: 'ES', label: 'ES' }, { value: 'GO', label: 'GO' },
  { value: 'MA', label: 'MA' }, { value: 'MT', label: 'MT' }, { value: 'MS', label: 'MS' },
  { value: 'MG', label: 'MG' }, { value: 'PA', label: 'PA' }, { value: 'PB', label: 'PB' },
  { value: 'PR', label: 'PR' }, { value: 'PE', label: 'PE' }, { value: 'PI', label: 'PI' },
  { value: 'RJ', label: 'RJ' }, { value: 'RN', label: 'RN' }, { value: 'RS', label: 'RS' },
  { value: 'RO', label: 'RO' }, { value: 'RR', label: 'RR' }, { value: 'SC', label: 'SC' },
  { value: 'SP', label: 'SP' }, { value: 'SE', label: 'SE' }, { value: 'TO', label: 'TO' },
];

export interface PessoaFisicaFormProps {
  data: Partial<PersonaFisicaFormData>;
  onChange: (data: Partial<PersonaFisicaFormData>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export const PessoaFisicaForm: React.FC<PessoaFisicaFormProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const { t } = useTranslation();

  const [showSpouseFields, setShowSpouseFields] = useState(
    data.identification?.maritalStatus === 'casado' ||
      data.identification?.maritalStatus === 'uniao_estavel'
  );

  const maritalStatusOptions = useMemo(() => [
    { value: 'solteiro', label: t('pessoaFisica.maritalStatusOptions.solteiro') },
    { value: 'casado', label: t('pessoaFisica.maritalStatusOptions.casado') },
    { value: 'uniao_estavel', label: t('pessoaFisica.maritalStatusOptions.uniao_estavel') },
    { value: 'divorciado', label: t('pessoaFisica.maritalStatusOptions.divorciado') },
    { value: 'viuvo', label: t('pessoaFisica.maritalStatusOptions.viuvo') },
  ], [t]);

  const genderOptions = useMemo(() => [
    { value: 'masculino', label: t('pessoaFisica.genderOptions.masculino') },
    { value: 'feminino', label: t('pessoaFisica.genderOptions.feminino') },
  ], [t]);

  const documentTypeOptions = useMemo(() => [
    { value: 'rg', label: t('pessoaFisica.documentTypeOptions.rg') },
    { value: 'cnh', label: t('pessoaFisica.documentTypeOptions.cnh') },
    { value: 'passaporte', label: t('pessoaFisica.documentTypeOptions.passaporte') },
  ], [t]);

  // Handle identification changes
  const handleIdentificationChange = useCallback(
    (field: keyof PersonIdentification) => (value: string) => {
      const newIdentification = {
        ...data.identification,
        [field]: value,
      } as PersonIdentification;

      // Show/hide spouse fields based on marital status
      if (field === 'maritalStatus') {
        const needsSpouse = value === 'casado' || value === 'uniao_estavel';
        setShowSpouseFields(needsSpouse);
        if (!needsSpouse) {
          newIdentification.spouseName = undefined;
          newIdentification.spouseCpf = undefined;
        }
      }

      onChange({
        ...data,
        identification: newIdentification,
      });
    },
    [data, onChange]
  );

  // Handle residential address changes
  const handleResidentialAddressChange = useCallback(
    (address: Partial<Address>) => {
      onChange({
        ...data,
        residentialAddress: address as Address,
      });
    },
    [data, onChange]
  );

  // Handle commercial address changes
  const handleCommercialAddressChange = useCallback(
    (address: Partial<Address>) => {
      onChange({
        ...data,
        commercialAddress: address as Address,
      });
    },
    [data, onChange]
  );

  // Handle "same as residential" checkbox
  const handleSameAsResidentialChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...data,
        sameAsResidential: checked,
        commercialAddress: checked ? { ...data.residentialAddress } as Address : ({} as Address),
      });
    },
    [data, onChange]
  );

  // Handle professional info changes
  const handleProfessionalChange = useCallback(
    (value: string) => {
      onChange({
        ...data,
        professional: {
          ...data.professional,
          mainActivity: value,
        },
      });
    },
    [data, onChange]
  );

  return (
    <div className="space-y-6">
      {/* Section 1: Identification */}
      <FormSection
        title={t('pessoaFisica.sectionIdentification')}
        description={t('pessoaFisica.sectionIdentificationDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label={t('pessoaFisica.fullName')}
              value={data.identification?.fullName || ''}
              onChange={handleIdentificationChange('fullName')}
              placeholder={t('pessoaFisica.fullNamePlaceholder')}
              error={errors['identification.fullName']}
              required
              disabled={disabled}
            />
          </div>

          <Input
            label={t('pessoaFisica.cpf')}
            value={data.identification?.cpf || ''}
            onChange={handleIdentificationChange('cpf')}
            mask={maskCPF}
            placeholder="000.000.000-00"
            error={errors['identification.cpf']}
            required
            disabled={disabled}
          />

          <Select
            label={t('pessoaFisica.maritalStatus')}
            value={data.identification?.maritalStatus || ''}
            onChange={handleIdentificationChange('maritalStatus')}
            options={maritalStatusOptions}
            error={errors['identification.maritalStatus']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaFisica.nationality')}
            value={data.identification?.nationality || ''}
            onChange={handleIdentificationChange('nationality')}
            placeholder={t('pessoaFisica.nationalityPlaceholder')}
            error={errors['identification.nationality']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaFisica.birthDate')}
            value={dateFromISO(data.identification?.birthDate || '')}
            onChange={(value) => {
              const masked = maskDate(value);
              const iso = dateToISO(masked);
              handleIdentificationChange('birthDate')(iso);
            }}
            placeholder="DD/MM/AAAA"
            error={errors['identification.birthDate']}
            required
            disabled={disabled}
          />

          <Select
            label={t('pessoaFisica.documentType')}
            value={data.identification?.documentType || ''}
            onChange={handleIdentificationChange('documentType')}
            options={documentTypeOptions}
            error={errors['identification.documentType']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaFisica.documentNumber')}
            value={data.identification?.documentNumber || ''}
            onChange={handleIdentificationChange('documentNumber')}
            placeholder={t('pessoaFisica.documentNumberPlaceholder')}
            error={errors['identification.documentNumber']}
            required
            disabled={disabled}
          />

          <Select
            label={t('pessoaFisica.documentUf')}
            value={data.identification?.documentUf || ''}
            onChange={handleIdentificationChange('documentUf')}
            options={BRAZILIAN_STATES}
            error={errors['identification.documentUf']}
            required
            disabled={disabled}
          />

          <RadioGroup
            name="gender"
            label={t('pessoaFisica.gender')}
            options={genderOptions}
            value={data.identification?.gender || ''}
            onChange={handleIdentificationChange('gender')}
            orientation="horizontal"
            error={errors['identification.gender']}
            required
            disabled={disabled}
          />

          <div className="md:col-span-2">
            <Input
              label={t('pessoaFisica.motherName')}
              value={data.identification?.motherName || ''}
              onChange={handleIdentificationChange('motherName')}
              placeholder={t('pessoaFisica.motherNamePlaceholder')}
              error={errors['identification.motherName']}
              required
              disabled={disabled}
            />
          </div>

          {showSpouseFields && (
            <>
              <Input
                label={t('pessoaFisica.spouseName')}
                value={data.identification?.spouseName || ''}
                onChange={handleIdentificationChange('spouseName')}
                placeholder={t('pessoaFisica.spouseNamePlaceholder')}
                error={errors['identification.spouseName']}
                required
                disabled={disabled}
              />

              <Input
                label={t('pessoaFisica.spouseCpf')}
                value={data.identification?.spouseCpf || ''}
                onChange={handleIdentificationChange('spouseCpf')}
                mask={maskCPF}
                placeholder="000.000.000-00"
                error={errors['identification.spouseCpf']}
                required
                disabled={disabled}
              />
            </>
          )}
        </div>
      </FormSection>

      {/* Section 2: Residential Address */}
      <FormSection
        title={t('pessoaFisica.sectionResidentialAddress')}
        description={t('pessoaFisica.sectionResidentialAddressDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        }
      >
        <AddressForm
          data={data.residentialAddress || {}}
          onChange={handleResidentialAddressChange}
          errors={pickErrors(errors, 'residentialAddress')}
          showPhones={true}
          disabled={disabled}
        />
      </FormSection>

      {/* Section 3: Commercial Address */}
      <FormSection
        title={t('pessoaFisica.sectionCommercialAddress')}
        description={t('pessoaFisica.sectionCommercialAddressDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
      >
        <Checkbox
          label={t('pessoaFisica.sameAsResidential')}
          checked={data.sameAsResidential || false}
          onChange={handleSameAsResidentialChange}
          disabled={disabled}
        />

        {!data.sameAsResidential && (
          <div className="mt-4">
            <AddressForm
              data={data.commercialAddress || {}}
              onChange={handleCommercialAddressChange}
              errors={pickErrors(errors, 'commercialAddress')}
              showPhones={false}
              disabled={disabled}
            />
          </div>
        )}
      </FormSection>

      {/* Section 4: Professional Info */}
      <FormSection
        title={t('pessoaFisica.sectionProfessional')}
        description={t('pessoaFisica.sectionProfessionalDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
      >
        <Input
          label={t('pessoaFisica.mainActivity')}
          value={data.professional?.mainActivity || ''}
          onChange={handleProfessionalChange}
          placeholder={t('pessoaFisica.mainActivityPlaceholder')}
          error={errors['professional.mainActivity']}
          required
          disabled={disabled}
        />
      </FormSection>
    </div>
  );
};

export default PessoaFisicaForm;
